import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { BookOpen, Compass, Users2, X, ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "@/store/AuthContext";
import { useMarkTutorialSeen } from "@/lib/hooks";

const STEPS = [
  {
    id: "today",
    icon: Sparkles,
    eyebrow: "Step 1 · Your rhythm",
    title: "Today’s timed habits, all in one place.",
    body: "The Today page shows every ritual you’ve added, in your own local hours, with a live countdown to the next one up.",
    hint: "Your circle can see the same rhythm — that’s how we nudge each other on time.",
  },
  {
    id: "library",
    icon: BookOpen,
    eyebrow: "Step 2 · The Library",
    title: "Pick rituals worth keeping.",
    body: "The Habit Library has 12 timed rituals — 5 to 90 minutes each. Add any of them to your rhythm; they’ll appear on the shared calendar.",
    hint: "Habits you pick tune the matching algorithm — your circle will drift closer to yours.",
  },
  {
    id: "community",
    icon: Users2,
    eyebrow: "Step 3 · Your circle",
    title: "A small group that shares your hours.",
    body: "You’re matched with a circle of ~6 whose age, timezone and habit overlap resemble yours. Send a soft nudge when their habit is up.",
    hint: "No streaks. No shame. Just people keeping time together.",
  },
];

export default function TutorialOverlay() {
  const { user, checkAuth } = useAuth();
  const location = useLocation();
  const mark = useMarkTutorialSeen();
  const [i, setI] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  // Only render once the user has onboarded and hasn't seen the tutorial yet.
  const shouldShow =
    !!user &&
    user.has_onboarded === true &&
    user.tutorial_seen === false &&
    location.pathname === "/" &&
    !dismissed;

  useEffect(() => {
    // Reset local step counter whenever the overlay becomes eligible.
    if (shouldShow) setI(0);
  }, [shouldShow]);

  if (!shouldShow) return null;

  const step = STEPS[i];
  const Icon = step.icon;
  const isLast = i === STEPS.length - 1;

  const persist = async () => {
    setDismissed(true);
    try {
      await mark.mutateAsync();
      await checkAuth();
    } catch {
      /* swallow — the local dismissal already hides it for this session */
    }
  };

  const next = () => {
    if (isLast) persist();
    else setI(i + 1);
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0 bg-charcoal/70 dark:bg-black/80 backdrop-blur-sm"
      data-testid="tutorial-overlay"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-lg rounded-3xl border border-stone-200 dark:border-[#3a2f22] bg-white dark:bg-[#221c17] text-stone-900 dark:text-[#E8D8C4] shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 pt-5">
          <span className="eyebrow">{step.eyebrow}</span>
          <button
            onClick={persist}
            className="p-1 rounded-full text-stone-500 hover:text-stone-800 dark:text-[#A6947A] dark:hover:text-[#E8D8C4]"
            aria-label="Dismiss tutorial"
            data-testid="tutorial-skip"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pt-3 pb-6">
          <div className="w-11 h-11 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center">
            <Icon size={20} />
          </div>
          <h2 className="font-serif text-3xl mt-4 leading-tight" data-testid={`tutorial-title-${step.id}`}>
            {step.title}
          </h2>
          <p className="text-sm text-stone-600 dark:text-[#A6947A] mt-3 leading-relaxed">{step.body}</p>
          <p className="text-xs text-stone-500 dark:text-[#8a7864] mt-3 italic">{step.hint}</p>

          <div className="flex items-center gap-1.5 mt-6" aria-label="Tutorial progress" data-testid="tutorial-progress">
            {STEPS.map((s, idx) => (
              <span
                key={s.id}
                className={`h-1 rounded-full transition-all ${
                  idx === i ? "w-8 bg-terracotta" : "w-4 bg-stone-200 dark:bg-[#3a2f22]"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-stone-100 dark:border-[#3a2f22] flex items-center justify-between gap-3 bg-stone-50/60 dark:bg-[#1a1512]">
          <button
            onClick={persist}
            className="text-xs text-stone-500 dark:text-[#A6947A] hover:underline"
            data-testid="tutorial-skip-text"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {i > 0 && (
              <button
                onClick={() => setI(i - 1)}
                className="btn-outline text-xs !min-h-[2.25rem] !py-1"
                data-testid="tutorial-back"
              >
                Back
              </button>
            )}
            {isLast ? (
              <Link
                to="/community"
                onClick={persist}
                className="btn-accent text-xs !min-h-[2.25rem] !py-1"
                data-testid="tutorial-finish"
              >
                Meet my circle <ArrowRight size={14} />
              </Link>
            ) : i === 1 ? (
              <Link
                to="/library"
                onClick={(e) => { e.preventDefault(); setI(i + 1); }}
                className="btn-accent text-xs !min-h-[2.25rem] !py-1"
                data-testid="tutorial-next"
              >
                Next <ArrowRight size={14} />
              </Link>
            ) : (
              <button
                onClick={next}
                className="btn-accent text-xs !min-h-[2.25rem] !py-1"
                data-testid="tutorial-next"
              >
                Next <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
